import * as SentryAngular from '@sentry/angular';
import * as Sentry from '@sentry/capacitor';
import type {ErrorEvent, Event, EventHint} from '@sentry/angular';
import {scrubBreadcrumb, scrubEvent, scrubRoute} from 'sentry-scrub';
import {HttpErrorResponse} from "@angular/common/http";
import {environment} from "../../environments/environment";
import {APP_VERSION} from "./app-version";
import {isUserCancelled} from "./error-filters";

/**
 * Starts Sentry in the browser and the native shells. Called from main.ts only, so it never runs
 * during SSR. An empty DSN leaves Sentry off, and every Sentry call elsewhere becomes a no-op.
 *
 * Uncaught errors (rejected promises, rxjs subscribe without an error callback, timer throws)
 * reach Sentry through its default window `error`/`unhandledrejection` handlers, silently. They
 * are deliberately not routed to Angular's ErrorHandler, because GlobalErrorHandle toasts.
 *
 * Every console.error is captured too (handled), so errors the app catches and only logs are not
 * lost.
 */
export function initSentry(): void {
  const {dsn, environment: sentryEnvironment} = environment.sentry;
  if (dsn === '') {
    return;
  }

  Sentry.init({
    dsn,
    environment: sentryEnvironment,
    release: `homey-app@${APP_VERSION}`,
    // Errors are sampled at 100% by default; tracing and replay stay off by not enabling them.
    // Trace ids still reach the API, through sentryTraceInterceptor.
    sendDefaultPii: false,
    // GlobalErrorHandle already reports failed HTTP calls the app didn't handle.
    enableCaptureFailedRequests: false,
    // Piet: drop nothing that is a real error, including what GlobalErrorHandle hides from users.
    // Mute noise with Sentry's inbound filters instead. Cancellations aren't errors at all.
    beforeSend: (event, hint) => isCancellation(event, hint) ? null : scrubEvent(event),
    // First, so Dedupe compares described HTTP errors, not identical "Object captured" events.
    // Namespaced: Sentry de-duplicates integrations by name.
    integrations: defaults => [
      {name: 'HomeyHttpErrorResponse', processEvent: describeHttpErrorResponse},
      ...defaults,
      SentryAngular.captureConsoleIntegration({levels: ['error']}),
    ],
    beforeBreadcrumb: scrubBreadcrumb,
  }, SentryAngular.init);
}

/**
 * Reports an error GlobalErrorHandle handled, whether or not it was shown, then console.errors
 * `logArgs`. Sentry marks it `handled: true`, which tells it apart from the silent uncaught ones.
 */
export function reportHandledError(error: unknown, ...logArgs: unknown[]): void {
  captureThenLog(error, undefined, logArgs);
}

/**
 * Captures `error`, then console.errors `logArgs`. The order matters: console capture would mark
 * an Error as caught before its copy is dropped, and the real capture would then be skipped. The
 * copy would otherwise be a second event (an HttpErrorResponse or a string is not deduplicated),
 * so every event raised while logging is dropped. Not filtered on `event.logger`: that is set
 * after this processor runs.
 */
function captureThenLog(error: unknown, hint: Parameters<typeof Sentry.captureException>[1], logArgs: unknown[]): void {
  Sentry.captureException(error, hint);
  Sentry.withScope(scope => {
    scope.addEventProcessor(() => null);
    console.error(...logArgs);
  });
}

/**
 * The cancellation itself, or a console.error of nothing but cancellations: captureConsole passes
 * an Error argument as the original exception and keeps every argument in `extra`.
 */
function isCancellation(event: ErrorEvent, hint: EventHint): boolean {
  if (isUserCancelled(hint.originalException)) {
    return true;
  }
  const consoleArguments = event.logger === 'console' ? event.extra?.['arguments'] : undefined;
  return Array.isArray(consoleArguments) && consoleArguments.length > 0 && consoleArguments.every(isUserCancelled);
}

/** A failed bootstrap leaves a blank screen and no later event to carry a breadcrumb. */
export function reportBootstrapError(error: unknown): void {
  captureThenLog(error, {mechanism: {type: 'bootstrap', handled: false}}, [error]);
}

export function setSentryUser(id: string | undefined): void {
  Sentry.setUser(id === undefined ? null : {id});
}

/**
 * HttpErrorResponse isn't an Error, so Sentry would title it "Object captured as exception" and
 * serialize it whole (URL with query string, response body). Both the handled and the uncaught
 * path come through here, so describe it once, by status and route.
 *
 * It runs as an integration rather than in beforeSend because Dedupe runs before beforeSend:
 * two different failed requests in a row would look identical to it and the second be dropped.
 */
function describeHttpErrorResponse(event: Event, hint: EventHint): Event {
  const error = hint.originalException;
  if (!(error instanceof HttpErrorResponse)) {
    return event;
  }
  const status = error.status.toString();
  const route = scrubRoute(error.url ?? 'unknown');
  // Keep the mechanism: it carries handled vs uncaught.
  const [original] = event.exception?.values ?? [];
  event.exception = {values: [{...original, type: 'HttpErrorResponse', value: `HTTP ${status} ${route}`}]};
  event.fingerprint = ['http-error', status, route];
  event.tags = {...event.tags, http_status: status};
  return event;
}

